import client from '@/utils/httpClient';
import { useInfiniteQuery } from 'react-query';
import { VirtualConferenceDashboardKeys } from './virtualConferenceDashboard.keys';

export interface VirtualConferenceMeeting {
  id: string;
  organizationName: string;
  conferenceName: string;
  conferenceStatus: string;
  meetingDateTime: string;
  caseCount: number;
  zoomMeetingId: number | null;
  recordingUrls: { url: string; fileType: string }[] | null;
}

export interface PaginationMeta {
  totalCount: number;
  pageSize: number;
  currentPageSize: number;
  currentStartIndex: number;
  currentEndIndex: number;
  pageNumber: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

export interface MeetingsQueryParams {
  sortOrder?: string;
  dateFrom?: string;
  dateTo?: string;
  organizationId?: string;
}

function getVirtualConferenceDashboardMeetings({
  pageParam = 1,
  sortOrder = '-MeetingDateTime',
  dateFrom,
  dateTo,
  organizationId,
}: {
  pageParam?: number;
  sortOrder?: string;
  dateFrom?: string;
  dateTo?: string;
  organizationId?: string;
}) {
  const params = new URLSearchParams({
    SortOrder: sortOrder,
    pageNumber: String(pageParam),
    PageSize: '50',
  });
  if (dateFrom) params.set('DateFrom', dateFrom);
  if (dateTo) params.set('DateTo', dateTo);
  if (organizationId) params.set('OrganizationId', organizationId);
  params.set('TimeZoneId', Intl.DateTimeFormat().resolvedOptions().timeZone);

  return client
    .get<VirtualConferenceMeeting[]>(`/virtualconferencedashboard?${params.toString()}`)
    .then((response) => ({
      data: response.data as VirtualConferenceMeeting[],
      pagination: JSON.parse(response.headers['x-pagination']) as PaginationMeta,
    }));
}

export function useGetVirtualConferenceDashboardMeetings(
  queryParams: MeetingsQueryParams,
  enabled = true,
) {
  const { sortOrder, dateFrom, dateTo, organizationId } = queryParams;

  return useInfiniteQuery(
    VirtualConferenceDashboardKeys.filteredList(sortOrder, dateFrom, dateTo, organizationId),
    ({ pageParam }) =>
      getVirtualConferenceDashboardMeetings({
        pageParam,
        sortOrder,
        dateFrom,
        dateTo,
        organizationId,
      }),
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.pagination.hasNext) {
          return lastPage.pagination.pageNumber + 1;
        }
        return undefined;
      },
      keepPreviousData: true,
      enabled,
    },
  );
}
